import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { FilaCronograma } from '@/lib/supabase/tipos-bd'
import { useColaboradoresActivos } from '@/modules/programacion/useHerramientas'
import { useCronograma } from '@/modules/tablero/useCronograma'
import { useVisitasProgramadas } from '@/modules/visitas/useVisitas'
import { ESTADOS_ORDEN_TRABAJO } from '@/shared/dominio/estados'
import { Modal } from '@/shared/ui/Modal/Modal'
import { Pastilla } from '@/shared/ui/Pastilla/Pastilla'
import { bloquesJornada, TRABAJA_DOMINGO } from '@/shared/utilidades/jornada'
import { iniciales } from '@/shared/utilidades/formato'

import estilos from './CalendarioDeTrabajo.module.css'

/**
 * EL CALENDARIO DE TODOS, DESDE LA MATRIZ.
 *
 * Johan, sobre la columna de Ejecución: «cuando yo abro ahí debe salir un
 * expandible con el calendario, como el que se ve en programación en la
 * cuenta de los técnicos», y después: «que sea como un emergente». El técnico
 * ve su mes con los días marcados; la oficina necesita lo mismo, de todos.
 *
 * HECHO PARA CUANDO HAY MÁS GENTE. La primera versión ponía un botón por
 * técnico y una tarjeta alta por trabajo: con cuatro técnicos ya se veía
 * apretado, y Johan lo dijo: «se ve feo, ¿qué pasa cuando hay más… más
 * gente?». Ahora:
 *   · el filtro es una lista desplegable, que no crece con la plantilla;
 *   · el día se agrupa POR TÉCNICO —la pregunta es quién va a dónde—, con un
 *     renglón compacto por trabajo;
 *   · la lista se desplaza por dentro y la ventana no crece;
 *   · al final, quién está libre ese día, que es lo que se busca al programar.
 *
 * Es la misma programación que guarda la Estación 3 (vista_cronograma). Un
 * trabajo de varios días aparece en cada día que ocupa, con los tramos de ese
 * día y el almuerzo por fuera (bloquesJornada, la jornada de la base).
 *
 * Y LAS VISITAS (0256). Desde la operación: «se asigna al técnico, pero
 * cuando se verifica la programación no aparece en el calendario». Ahora la
 * visita de cada orden —ir a mirar y cotizar, o la de certificación— sale
 * aquí con su hora, marcada como visita.
 */

/** Un renglón del calendario: un trabajo, o una visita con la misma forma. */
type Fila = FilaCronograma & { visita?: boolean }

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]
const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
const SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']

/** aaaa-mm-dd en hora local, que es la de la gente (Colombia). */
function claveDia(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

function tituloDelDia(dia: string): string {
  const [a, m, d] = dia.split('-').map(Number)
  return `${SEMANA[new Date(a, m - 1, d).getDay()]}, ${d} de ${MESES[m - 1]}`
}

/** «07:00»: en 24 horas cabe en una línea y no hay a. m. / p. m. que leer. */
function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** Lo que un trabajo tiene en un día: sus tramos de trabajo de ese día. */
type TrabajoDelDia = {
  fila: Fila
  tramos: { inicio: Date; fin: Date }[]
  /** «día 2 de 3» cuando la obra ocupa más de uno. */
  parte: { dia: number; de: number } | null
}

/** Reparte cada trabajo programado en los días que ocupa. */
function repartirPorDia(filas: Fila[]): Map<string, TrabajoDelDia[]> {
  const mapa = new Map<string, TrabajoDelDia[]>()

  for (const fila of filas) {
    if (!fila.fecha_inicio_programada) continue

    const porDia = new Map<string, { inicio: Date; fin: Date }[]>()
    // Una visita es un rato seguido, no una jornada de obra: no se parte
    // en tramos ni se pasa al otro día (0256).
    if (fila.visita) {
      const inicio = new Date(fila.fecha_inicio_programada)
      const fin = new Date(inicio.getTime() + Number(fila.horas_asignadas ?? 1) * 3_600_000)
      porDia.set(claveDia(inicio), [{ inicio, fin }])
    }
    for (const s of fila.visita
      ? []
      : bloquesJornada(
          fila.fecha_inicio_programada,
          // La base las manda como texto («8.00»).
          fila.horas_asignadas === null ? null : Number(fila.horas_asignadas),
        )) {
      if (s.tipo !== 'trabajo') continue
      const clave = claveDia(s.inicio)
      porDia.set(clave, [...(porDia.get(clave) ?? []), { inicio: s.inicio, fin: s.fin }])
    }
    // Sin horas asignadas no hay cómo partirlo: va el día en que arranca.
    if (porDia.size === 0) {
      const inicio = new Date(fila.fecha_inicio_programada)
      porDia.set(claveDia(inicio), [{ inicio, fin: inicio }])
    }

    const dias = [...porDia.keys()].sort()
    dias.forEach((dia, i) => {
      mapa.set(dia, [
        ...(mapa.get(dia) ?? []),
        {
          fila,
          tramos: porDia.get(dia)!,
          parte: dias.length > 1 ? { dia: i + 1, de: dias.length } : null,
        },
      ])
    })
  }

  for (const lista of mapa.values()) {
    lista.sort((a, b) => a.tramos[0].inicio.getTime() - b.tramos[0].inicio.getTime())
  }
  return mapa
}

/** Horas de trabajo de esos tramos, para el total del técnico en el día. */
function horasDe(tramos: { inicio: Date; fin: Date }[]): number {
  return tramos.reduce((s, t) => s + (t.fin.getTime() - t.inicio.getTime()) / 3_600_000, 0)
}

export function CalendarioDeTrabajo({ onCerrar }: { onCerrar: () => void }) {
  const navegar = useNavigate()
  const { filas: trabajos, cargando, error } = useCronograma()
  const { visitas } = useVisitasProgramadas()
  const filas = useMemo<Fila[]>(
    () => [
      ...trabajos,
      ...visitas.map((v) => ({
        id: `visita-${v.orden_servicio_id}`,
        numero: v.numero,
        estado: (v.hecha ? 'finalizada' : 'programada') as FilaCronograma['estado'],
        orden_servicio_id: v.orden_servicio_id,
        orden_servicio_numero: v.numero,
        cliente_nombre: v.cliente,
        servicio_codigo: v.servicio_codigo,
        servicio_nombre: v.servicio_nombre,
        zona_nombre: v.zona,
        colaborador_id: v.colaborador_id,
        colaborador_nombre: v.colaborador_nombre,
        horas_asignadas: v.visita_horas ?? 1,
        horas_ejecutadas: 0,
        horas_extra: 0,
        diferencia_horas: null,
        fecha_inicio_programada: v.visita_inicio,
        fecha_fin_programada: null,
        fecha_inicio_real: null,
        fecha_fin_real: null,
        retraso_horas: null,
        atrasada: false,
        arranque_vencido: false,
        visita: true,
      })),
    ],
    [trabajos, visitas],
  )
  const { colaboradores } = useColaboradoresActivos()

  const [hoy] = useState(() => claveDia(new Date()))
  const [mesAncla, setMesAncla] = useState(() => hoy.slice(0, 7))
  const [seleccion, setSeleccion] = useState(hoy)
  const [tecnico, setTecnico] = useState<string>('')

  const porDia = useMemo(
    () => repartirPorDia(tecnico ? filas.filter((f) => f.colaborador_id === tecnico) : filas),
    [filas, tecnico],
  )

  const [anio, mes] = mesAncla.split('-').map(Number)
  const celdas = useMemo(() => {
    const primero = new Date(anio, mes - 1, 1)
    const dias = new Date(anio, mes, 0).getDate()
    const lista: (string | null)[] = Array.from({ length: (primero.getDay() + 6) % 7 }, () => null)
    for (let d = 1; d <= dias; d++) lista.push(`${mesAncla}-${String(d).padStart(2, '0')}`)
    return lista
  }, [anio, mes, mesAncla])

  function moverMes(delta: number) {
    const nuevo = new Date(anio, mes - 1 + delta, 1)
    setMesAncla(`${nuevo.getFullYear()}-${String(nuevo.getMonth() + 1).padStart(2, '0')}`)
  }

  // El día elegido, agrupado por técnico.
  const grupos = useMemo(() => {
    const mapa = new Map<string, { nombre: string; trabajos: TrabajoDelDia[] }>()
    for (const t of porDia.get(seleccion) ?? []) {
      const clave = t.fila.colaborador_id ?? 'sin'
      const g = mapa.get(clave) ?? { nombre: t.fila.colaborador_nombre, trabajos: [] }
      g.trabajos.push(t)
      mapa.set(clave, g)
    }
    return [...mapa.entries()]
      .map(([id, g]) => ({
        id,
        ...g,
        horas: g.trabajos.reduce((s, t) => s + horasDe(t.tramos), 0),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre))
  }, [porDia, seleccion])

  // Quién no tiene nada ese día. Solo con «Todos»: filtrando por uno, la
  // pregunta ya está contestada arriba.
  const libres = useMemo(() => {
    if (tecnico) return []
    const ocupados = new Set(grupos.map((g) => g.id))
    return colaboradores.filter((c) => !ocupados.has(c.id))
  }, [colaboradores, grupos, tecnico])

  const [, , diaNum] = seleccion.split('-').map(Number)
  // Domingo sin trabajo solo si la jornada no lo trabaja (0266: sí se trabaja).
  const esDomingo = !TRABAJA_DOMINGO && new Date(anio, mes - 1, diaNum).getDay() === 0

  return (
    <Modal
      abierto
      ancho
      titulo="Calendario de trabajo"
      subtitulo="Lo programado de todos los técnicos. Toque un día para ver quién va a dónde."
      onCerrar={onCerrar}
    >
      {error ? (
        <p className={estilos.sinNada}>No se pudo leer la programación: {error}</p>
      ) : (
        <div className={estilos.cuerpo}>
          <div className={estilos.mes}>
            <select
              className={estilos.selector}
              value={tecnico}
              onChange={(e) => setTecnico(e.target.value)}
              aria-label="Ver un solo técnico"
            >
              <option value="">Todos los técnicos</option>
              {colaboradores.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>

            <div className={estilos.mesTope}>
              <button
                type="button"
                className={estilos.flecha}
                onClick={() => moverMes(-1)}
                aria-label="Mes anterior"
              >
                ‹
              </button>
              <span className={estilos.mesNombre}>
                {MESES[mes - 1]} {anio}
              </span>
              <button
                type="button"
                className={estilos.flecha}
                onClick={() => moverMes(1)}
                aria-label="Mes siguiente"
              >
                ›
              </button>
            </div>

            <div className={estilos.semana}>
              {DIAS.map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>

            <div className={estilos.rejilla}>
              {celdas.map((dia, i) => {
                if (dia === null) return <span key={`v${i}`} />
                const cuantos = porDia.get(dia)?.length ?? 0
                return (
                  <button
                    key={dia}
                    type="button"
                    className={`${estilos.celda} ${dia === hoy ? estilos.hoy : ''} ${
                      dia === seleccion ? estilos.elegido : ''
                    }`}
                    onClick={() => setSeleccion(dia)}
                    aria-label={`${tituloDelDia(dia)}: ${cuantos} trabajos`}
                  >
                    <span className={estilos.numeroDia}>{Number(dia.slice(-2))}</span>
                    {/* Un punto por trabajo, hasta tres; con más, la cifra. */}
                    <span className={estilos.puntos} aria-hidden="true">
                      {cuantos > 3 ? (
                        <span className={estilos.muchos}>{cuantos}</span>
                      ) : (
                        Array.from({ length: cuantos }, (_, k) => (
                          <span key={k} className={estilos.punto} />
                        ))
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className={estilos.dia}>
            <p className={estilos.diaTitulo}>
              {tituloDelDia(seleccion)}
              {grupos.length > 0 && (
                <span className={estilos.diaCuenta}>
                  {grupos.length} {grupos.length === 1 ? 'técnico' : 'técnicos'} ·{' '}
                  {grupos.reduce((s, g) => s + g.trabajos.length, 0)} trabajos
                </span>
              )}
            </p>

            <div className={`${estilos.desplazable} desplazable`}>
              {cargando ? (
                <p className={estilos.sinNada}>Cargando la programación…</p>
              ) : grupos.length === 0 ? (
                <p className={estilos.sinNada}>
                  {esDomingo ? 'Domingo: no se trabaja.' : 'Nada programado ese día.'}
                </p>
              ) : (
                grupos.map((g) => (
                  <section key={g.id} className={estilos.grupo}>
                    <header className={estilos.grupoTope}>
                      <span className={estilos.avatar} aria-hidden="true">
                        {iniciales(g.nombre)}
                      </span>
                      <span className={estilos.grupoNombre}>{g.nombre}</span>
                      <span className={estilos.grupoHoras}>
                        {g.horas.toFixed(1).replace('.', ',').replace(',0', '')} h
                      </span>
                    </header>

                    <ul className={estilos.lista}>
                      {g.trabajos.map(({ fila, tramos, parte }) => (
                        <li key={fila.id}>
                          <button
                            type="button"
                            className={estilos.trabajo}
                            onClick={() => navegar(`/ordenes/${fila.orden_servicio_id}`)}
                            title="Abrir la orden"
                          >
                            <span className={estilos.horario}>
                              {tramos
                                .map((t) =>
                                  t.fin > t.inicio
                                    ? `${hhmm(t.inicio)}–${hhmm(t.fin)}`
                                    : hhmm(t.inicio),
                                )
                                .join(' · ')}
                            </span>
                            <span className={estilos.que}>
                              <span className={estilos.cliente}>
                                {fila.cliente_nombre ?? 'Sin cliente'}
                              </span>
                              <span className={estilos.detalle}>
                                {fila.numero}
                                {fila.servicio_codigo && ` · ${fila.servicio_codigo}`}
                                {parte && ` · día ${parte.dia} de ${parte.de}`}
                              </span>
                            </span>
                            {fila.visita ? (
                              <Pastilla tono="compuerta">
                                {fila.estado === 'finalizada' ? 'Visita hecha' : 'Visita'}
                              </Pastilla>
                            ) : (
                              <Pastilla tono={ESTADOS_ORDEN_TRABAJO[fila.estado].tono}>
                                {ESTADOS_ORDEN_TRABAJO[fila.estado].etiqueta}
                              </Pastilla>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))
              )}

              {!cargando && !esDomingo && libres.length > 0 && (
                <p className={estilos.libres}>
                  <b>Libres ese día:</b> {libres.map((c) => c.nombre).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
